import { RunDto } from "@custom-types/modelRun"
import { GraphDataViewModel, GroupNodeLabel } from "@features/graph/graphViewModel"
import {
  graphSampleNodeToViewGroupNodeLabel,
  graphViewGroupNodeLabelToSampleNode,
} from "@features/graph/utils/graphUtils"
import { TimeSeriesChart } from "@features/results/Result/ChartsContainer/TimeSeriesChart"
import { DashboardChartContainer } from "@features/results/Result/Dashboard/DashboardChartContainer"
import {
  ChartGroupLayout,
  DashboardTimeSeriesChartDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import { CircularProgress } from "@mui/material"
import {
  useGetRunsNodesResultsHistoricAggregateQuery,
  useGetRunsNodesResultsHistoricQuery,
} from "@store/services/runService"
import React, { FunctionComponent, useMemo, useRef } from "react"
import logger from "@utils/logger"
import { useDashboardConfig } from "@features/results/Result/Dashboard/dashboardConfig"
import { calculateLagShift } from "@utils/run"
import { useIntersectionObserver } from "@hooks/useIntersectionObserver"

interface Props {
  chart: DashboardTimeSeriesChartDto
  runs: RunDto[]
  layout?: ChartGroupLayout
}

export const DashboardTimeSeriesChart: FunctionComponent<Props> = (props) => {
  const [cfg, updateCfg] = useDashboardConfig({
    showLegend: true,
    selectedNodes: props.chart.Nodes || props.chart.NodeSelect?.[0]?.Nodes || [],
    hiddenDatasets: [],
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const isVisible = useIntersectionObserver(containerRef)

  const selectedNodes = cfg.selectedNodes ?? []

  const handleShowLegend = () => {
    updateCfg((draft) => {
      draft.showLegend = !draft.showLegend
    })
  }

  const handleOptionChange = (nodes: string[]) => {
    updateCfg((draft) => {
      draft.selectedNodes = nodes
    })
  }

  const handleLegendClick = (datasetLabel: string) => {
    updateCfg((draft) => {
      const hiddenDatasets = draft.hiddenDatasets ?? []
      if (hiddenDatasets.includes(datasetLabel)) {
        draft.hiddenDatasets = hiddenDatasets.filter((label) => label !== datasetLabel)
      } else {
        draft.hiddenDatasets = [...hiddenDatasets, datasetLabel]
      }
    })
  }

  // Filter as non-existing node is causing 500
  const nodeLabels: GroupNodeLabel[] = selectedNodes
    .filter((n) => GraphDataViewModel.groupLabels.some((d) => d.label === n))
    .map(graphSampleNodeToViewGroupNodeLabel)

  const preservedNodes = nodeLabels.map((x) => x.label)
  if (selectedNodes?.length !== nodeLabels.length) {
    // #537560 no result as Alexandria_- OPEX != Alexandria__- OPEX
    function difference<T>(array1: T[], array2: T[]): T[] {
      return array1.filter((item) => !array2.includes(item))
    }

    function joinDifferences(array1: string[], array2: string[]): string {
      const diff = difference(array1, array2)
      return diff.map((item) => `"${item}"`).join(", ") // Wrapping each item in quotes
    }

    logger.debug(
      `Filtered: ${joinDifferences(
        selectedNodes,
        preservedNodes,
      )} +  as this/these nodes don't exist for chart: '${props.chart.Title}'`,
    )
  }

  const runIds = props.runs.map((run) => run.key)
  const { isFetching: isFetchingHistoricAggregateData, data: aggregateData } =
    useGetRunsNodesResultsHistoricAggregateQuery(
      {
        runKeys: runIds,
        nodes: selectedNodes.map(graphViewGroupNodeLabelToSampleNode),
      },
      {
        skip: selectedNodes.length === 0 || !isVisible,
      },
    )
  const { isFetching: isFetchingHistoricData, data: historicResponse } =
    useGetRunsNodesResultsHistoricQuery(
      {
        runKeys: runIds,
        nodes: selectedNodes.map(graphViewGroupNodeLabelToSampleNode),
      },
      {
        skip: selectedNodes.length === 0 || !isVisible,
      },
    )
  const isFetching = isFetchingHistoricAggregateData || isFetchingHistoricData

  const lagShift = useMemo(
    () => calculateLagShift(historicResponse?.results),
    [historicResponse?.results],
  )

  const handleHistoricDataToggle = () => {
    if (!aggregateData) return

    updateCfg((draft) => {
      draft.showHistoric = !draft.showHistoric
    })
  }

  const handleShowAsTableToggle = (newValue: boolean) => {
    updateCfg((draft) => {
      draft.showAsTable = newValue
    })
  }

  const handleFullscreen = () => {
    updateCfg((draft) => {
      draft.isFullscreen = !draft.isFullscreen
    })
  }

  const showMean = props.chart.Aggregate === "mean"
  const showStDev = showMean && props.chart.ShowStandardDeviations

  return (
    <DashboardChartContainer
      chart={props.chart}
      layout={props.layout}
      onOptionChange={(v) => handleOptionChange(v.Nodes)}
      onShowAsTableToggle={handleShowAsTableToggle}
      onFullScreen={handleFullscreen}
      showAsTable={cfg.showAsTable}
      onShowHistoricDataToggle={handleHistoricDataToggle}
      showHistoricData={cfg.showHistoric}
      runIds={runIds}
      onShowLegendToggle={handleShowLegend}
      showLegend={cfg.showLegend}
      ref={containerRef}
    >
      {isFetching ? (
        <div
          style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}
        >
          <CircularProgress />
        </div>
      ) : (
        isVisible && (
          <TimeSeriesChart
            title={props.chart.Title}
            nodes={nodeLabels}
            isMultipleNodesMode={true}
            showMeanAggregates={showMean}
            showMeanLine={showMean}
            showStDev={showStDev}
            maxHeight="100%"
            showHistoric={cfg.showHistoric}
            percentiles={[]}
            historicNodes={historicResponse?.results}
            lagShift={lagShift}
            ref={undefined}
            runs={props.runs}
            maintainAspectRatio={false}
            legendPosition={props.chart.LegendPosition}
            showAsTable={cfg.showAsTable}
            aggregateData={aggregateData}
            showLegend={cfg.showLegend}
            hiddenDatasets={cfg.hiddenDatasets}
            onLegendClick={handleLegendClick}
          />
        )
      )}
    </DashboardChartContainer>
  )
}
